import { useReducedMotion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useLayoutEffect, useRef, useState } from 'react'

import { DURATION, EASE } from './tokens'

const HEIGHT_TRANSITION = `height ${DURATION.slow}s cubic-bezier(${EASE.standard.join(', ')})`

// Lässt die Höhe gleiten, wenn animateKey wechselt, zum Beispiel zwischen zwei
// Schritten eines Dialogs. Die alte Höhe bleibt stehen, bis der neue Inhalt
// da ist, dann gleitet sie zu dessen Höhe und ist danach wieder auto.
// Alle anderen Änderungen folgen sofort, etwa Fehlertexte oder Inhalt, der
// beim Öffnen des Dialogs nachlädt. Sonst schneidet overflow hidden den
// Inhalt ab, und ein zentrierter Dialog wandert beim Einblenden.
// Bewusst eine CSS-Transition statt framer-motion. framer misst „auto“ per
// getBoundingClientRect, und das ist beim Einblenden eines Dialogs auf 95 %
// skaliert. Die Höhe startete deshalb zu klein, schnitt den unteren Button ab
// und wuchs dann nach. Von auto aus gibt es in CSS keinen Übergang, das
// Festhalten der alten Höhe passiert also sofort.
// Ohne ResizeObserver (jsdom) und bei reduzierter Bewegung bleibt die Höhe auto.
const AnimatedHeight = ({ animateKey, children, className }) => {
  const reducedMotion = useReducedMotion()
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const lastKey = useRef(animateKey)
  const isSwapping = useRef(false)
  const [height, setHeight] = useState('auto')

  const canAnimate = !reducedMotion && typeof ResizeObserver !== 'undefined'

  const finishSwap = () => {
    isSwapping.current = false
    setHeight('auto')
  }

  // Läuft vor dem Paint, die alte Höhe steht also schon beim ersten Bild
  // des Wechsels
  useLayoutEffect(() => {
    if (lastKey.current === animateKey) return undefined
    lastKey.current = animateKey
    if (!canAnimate) return undefined

    isSwapping.current = true
    setHeight(containerRef.current.offsetHeight)

    // Hat der neue Inhalt dieselbe Höhe, meldet der ResizeObserver nichts und
    // es gibt kein transitionend. Dann gibt dieser Fallback die Höhe frei.
    const fallback = setTimeout(
      () => {
        const container = containerRef.current
        const content = contentRef.current
        if (!isSwapping.current || !container || !content) return
        if (container.offsetHeight === content.offsetHeight) finishSwap()
      },
      (DURATION.base + DURATION.slow) * 1000 + 100
    )

    return () => clearTimeout(fallback)
  }, [animateKey, canAnimate])

  useLayoutEffect(() => {
    const element = contentRef.current
    if (!element || typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(() => {
      if (isSwapping.current) setHeight(element.offsetHeight)
    })
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  const handleTransitionEnd = (event) => {
    // transitionend von Buttons und Feldern im Inhalt blubbert hier hoch
    if (event.target !== event.currentTarget) return
    if (event.propertyName !== 'height') return
    finishSwap()
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={
        height === 'auto'
          ? undefined
          : { height, overflow: 'hidden', transition: HEIGHT_TRANSITION }
      }
      onTransitionEnd={handleTransitionEnd}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  )
}

AnimatedHeight.propTypes = {
  animateKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}

AnimatedHeight.defaultProps = {
  className: undefined
}

export default AnimatedHeight
